import moment from 'moment';
import isEqual from 'lodash/isEqual';
import { IRNSummaryDetails } from '../common/types';
import { getCursor, writeData } from './db';
import { appConfig } from '../config/app-config';
import {
  CursorAccess,
  DATE_FORMAT,
  DATE_TIME_FORMAT,
  MIS_METADATA_INDEX,
  QueryType,
} from './constants';
import { MisMetadataEntity } from '../common/entity';
import { Query } from './types';
import { getAllDatesBetweenTwoDates } from './utils';

const isEqualIRNData = (
  oldData: MisMetadataEntity,
  newData: IRNSummaryDetails
): boolean => {
  const oldIRNData: IRNSummaryDetails = {
    totalNoOfInvoices: oldData.totalNoOfInvoices,
    totalPendingIrnGeneration: oldData.totalPendingIrnGeneration,
    totalIrnGenerated: oldData.totalIrnGenerated,
    totalIrnFailed: oldData.totalIrnFailed,
    totalIrnCancelled: oldData.totalIrnCancelled,
  };
  return isEqual(oldIRNData, newData);
};

export const createOrUpdateMetadata = async (
  irnDayWiseMetadata: Map<string, IRNSummaryDetails>,
  orgId: string
) => {
  try {
    for (const [date, irnDaySummary] of irnDayWiseMetadata) {
      const dateObj = moment(date, DATE_FORMAT).toDate();
      let cursor = await getCursor(
        appConfig.IDB.STORE.MIS_METADATA,
        MIS_METADATA_INDEX.DATE_INDEX.NAME,
        dateObj,
        CursorAccess.READ_WRITE,
        QueryType.ONLY
      );

      while (cursor) {
        if (cursor.value.orgId === orgId) {
          if (isEqualIRNData(cursor.value, irnDaySummary)) break;
          const updatedData = Object.assign(cursor.value, irnDaySummary);
          // @ts-ignore
          cursor.update(updatedData);
          break;
        }
        cursor = await cursor.continue();
      }
      if (!cursor) {
        const misMetadata: MisMetadataEntity = {
          date: dateObj,
          orgId,
          ...irnDaySummary,
        };
        await writeData(appConfig.IDB.STORE.MIS_METADATA, misMetadata);
      }
    }
  } catch (e) {
    // console.log('Failed to write data to idb', e);
  }
};

export const getMetadataFromDb = async (
  invoiceStartDate: string,
  invoiceEndDate: string,
  orgId: string
) => {
  try {
    const startDate = moment(invoiceStartDate, DATE_TIME_FORMAT).format(
      DATE_FORMAT
    );
    const endDate = moment(invoiceEndDate, DATE_TIME_FORMAT).format(
      DATE_FORMAT
    );

    const query: Query = {
      lowerBound: moment(startDate, DATE_FORMAT).toDate(),
      upperBound: moment(endDate, DATE_FORMAT).toDate(),
    };

    let cursor = await getCursor(
      appConfig.IDB.STORE.MIS_METADATA,
      MIS_METADATA_INDEX.DATE_INDEX.NAME,
      query,
      CursorAccess.READ_ONLY,
      QueryType.BOUND
    );

    const irnDayWiseMetadata = new Map<string, IRNSummaryDetails>();
    while (cursor) {
      if (cursor.value.orgId === orgId) {
        const metadata: IRNSummaryDetails = {
          totalPendingIrnGeneration: cursor.value.totalPendingIrnGeneration,
          totalNoOfInvoices: cursor.value.totalNoOfInvoices,
          totalIrnGenerated: cursor.value.totalIrnGenerated,
          totalIrnFailed: cursor.value.totalIrnFailed,
          totalIrnCancelled: cursor.value.totalIrnCancelled,
        };
        irnDayWiseMetadata.set(
          moment(cursor.value.date).format(DATE_FORMAT),
          metadata
        );
      }
      cursor = await cursor.continue();
    }
    const totalDates = getAllDatesBetweenTwoDates(
      moment(startDate, DATE_FORMAT),
      moment(endDate, DATE_FORMAT)
    );
    if (irnDayWiseMetadata.size === totalDates.length) {
      return irnDayWiseMetadata;
    }
  } catch (e) {
   //  console.log('Failed to write data to idb', e);
  }
};
